import map from 'lodash/map.js';
import merge from 'lodash/merge.js';
import orderBy from 'lodash/orderBy.js';
import unset from 'lodash/unset.js';

import axios from '@/plugins/axios.js';
import ActivityAnnouncement from '@/store/modules/activityannouncements/ActivityAnnouncement.js';
import Collection from '@/store/quill/Collection.js';
import { promiseError, toggleLoading } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default merge(Collection('ActivityAnnouncements', ActivityAnnouncement), {
    state: {
        activity_updates: {},
    },

    getters: {
        all(state) {
            return () => {
                const items = { ...state };
                unset(items, 'activity_updates');
                unset(items, 'loading');
                unset(items, 'sort');
                unset(items, 'filter');
                unset(items, 'paginate');

                return items;
            };
        },

        sorted(state, getters) {
            return orderBy(getters.all(), 'created_at', 'desc');
        },
    },

    actions: {
        async fetch(context, { activityId }) {
            toggleLoading(context);

            try {
                const { data } = await context.dispatch('fetchAll', {
                    filter: { key: 'activity_id', value: activityId },
                    include: ['media', 'creator'],
                    endpoint: 'activity-updates',
                });

                toggleLoading(context);

                return Promise.all([
                    context.dispatch('sort', { created_at: 'desc' }),
                    context.dispatch('updatePageMeta', data.meta),
                    context.dispatch('hydrate', data.data),
                ]).then(() => data.data);
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'activity_announcements_load_error',
                    context,
                });

                return error;
            }
        },

        async fetchAllActivityUpdates({ commit, dispatch, rootGetters }, courseId) {
            try {
                const sorted = {};

                const { data } = await dispatch('fetchAll', {
                    filter: { key: 'course_id', value: courseId },
                    include: ['media', 'creator', 'activity'],
                    raw: { per_page: 100 },
                    endpoint: 'activity-updates',
                });

                const activities = map(data.data, item => item.activity_id);

                activities.forEach(activity => {
                    sorted[activity] = {};
                    sorted[activity].data = [];
                    sorted[activity].activity_id = activity;

                    data.data.forEach(item => {
                        if (item.activity_id === activity) {
                            sorted[activity].title = item.activity.title;
                            sorted[activity].data.push(item);
                        }
                    });
                });

                return commit('HYDRATE_ACTIVITY_UPDATES', sorted);
            } catch (_error) {}
        },

        async create(context, payload) {
            toggleLoading(context);

            const queryBuilder = new QueryBuilder('activity-updates');

            try {
                const { data } = await axios.post(queryBuilder.toUrl(), payload);

                context.dispatch('add', data.data);

                return data.data;
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'activity_announcements_create_error',
                    context,
                });

                return error;
            }
        },

        async deleteAnnouncement(context, post) {
            toggleLoading(context);

            const queryBuilder = new QueryBuilder(`activity-updates/${post.id}`);

            try {
                const { data } = await axios.delete(queryBuilder.toUrl(), {
                    errorHandle: false,
                });

                await context.dispatch('remove', post.id);

                return data.data;
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'activity_announcements_delete_error',
                    context,
                });

                throw error;
            }
        },
    },

    mutations: {
        HYDRATE_ACTIVITY_UPDATES(state, activities) {
            state.activity_updates = activities;
        },

        PURGE_ACTIVITY_UPDATES(state) {
            state.activity_updates = {};
        },
    },
});
